import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../Dialog/Dialog'
import { Button } from '../Button/Button'
import { Input } from '../Input/Input'
import { ColorPopover, rgba } from './WorkbenchColorPicker'
import { WIDTHS, BitsPopover } from './WorkbenchFloatingBits'
import { paramLabel } from './indicators'
import { getIndicator } from './types'
import type { IndicatorInstance, IndicatorOption, IndicatorSettings } from './types'

// Per-indicator settings popup (one screen, no tabs). Reuses the SAME controls
// as the chart/drawing settings: the ColorPopover (swatch grid + opacity) and
// the WIDTHS thickness buttons — nothing here is a new style. Edits apply live
// through onChange; Cancel restores the snapshot taken on mount.

export interface WorkbenchIndicatorSettingsProps {
  chart: Chart
  instance: IndicatorInstance
  option: IndicatorOption
  onChange: (patch: Partial<IndicatorSettings>) => void
  onClose: () => void
}

/** A stored colour can be hex (default) or rgba (after the picker adds opacity).
 * Parse it into the { hex, op } pair the ColorPopover expects. */
function parseColor(c: string | undefined): { hex: string; op: number } {
  if (c && c.startsWith('rgba')) {
    const m = c.match(/[\d.]+/g) ?? []
    const [r = '255', g = '150', b = '0', a = '1'] = m
    const hex =
      '#' + [r, g, b].map((n) => Math.round(Number(n)).toString(16).padStart(2, '0')).join('')
    return { hex, op: Math.round(Number(a) * 100) }
  }
  return { hex: c || '#FF9600', op: 100 }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-body text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

function NumberField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <Input
      type="number"
      min={1}
      value={value}
      onChange={(e) => {
        const v = Number(e.target.value)
        if (!Number.isNaN(v) && v >= 1) onChange(v)
      }}
      className="h-8 w-28 px-2 text-body text-foreground tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
    />
  )
}

/** Colour swatch that opens the shared ColorPopover (grid + opacity). Stores rgba. */
function ColorField({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const { hex, op } = parseColor(value)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Colour"
        className="h-7 w-10 rounded-md border border-border cursor-pointer"
        style={{ background: value }}
      />
      {open && (
        <BitsPopover align="right">
          <ColorPopover
            curColor={hex}
            curOpacity={op}
            onColor={(c) => onChange(rgba(c, op))}
            onOpacity={(v) => onChange(rgba(hex, v))}
          />
        </BitsPopover>
      )}
    </div>
  )
}

/** Thickness — the same WIDTHS + line-preview buttons as the drawing toolbar. */
function ThicknessField({ value, onChange }: { value: number; onChange: (w: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {WIDTHS.map((w) => (
        <button
          key={w}
          type="button"
          onClick={() => onChange(w)}
          aria-label={`Thickness ${w}`}
          className={cn(
            'inline-flex h-8 w-9 items-center justify-center rounded-md border cursor-pointer transition-colors',
            value === w ? 'border-primary bg-muted' : 'border-border hover:bg-muted/60',
          )}
        >
          <span className="block w-4 bg-foreground" style={{ height: w }} />
        </button>
      ))}
    </div>
  )
}

export function WorkbenchIndicatorSettings({
  chart,
  instance,
  option,
  onChange,
  onClose,
}: WorkbenchIndicatorSettingsProps) {
  // Snapshot the settings on mount so Cancel can revert (edits preview live).
  const originalRef = useRef<IndicatorSettings | null>(null)
  if (originalRef.current === null) {
    originalRef.current = JSON.parse(JSON.stringify(instance.settings)) as IndicatorSettings
  }

  const s = instance.settings
  const lineCount = Math.max(
    1,
    getIndicator(chart, instance.paneId, instance.engineName)?.figures?.length || 1,
  )

  const handleCancel = () => {
    if (originalRef.current) onChange(originalRef.current)
    onClose()
  }
  const handleSave = () => onClose()

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) handleCancel() // Esc / X behave like Cancel
      }}
    >
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayClassName="bg-black/60"
        className="v1 bg-popover max-w-[420px] sm:max-w-[420px] w-[92vw] p-0 gap-0 overflow-visible"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
          <DialogTitle className="text-section text-foreground">{option.label}</DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3">
          {s.params.map((value, i) => (
            <Row key={i} label={paramLabel(instance.name, i)}>
              <NumberField
                value={value}
                onChange={(v) => {
                  const params = [...s.params]
                  params[i] = v
                  onChange({ params })
                }}
              />
            </Row>
          ))}

          <div className="my-2 h-px bg-border" />

          {Array.from({ length: lineCount }).map((_, i) => (
            <Row key={i} label={lineCount > 1 ? `Line ${i + 1}` : 'Color'}>
              <ColorField
                value={s.colors[i] ?? '#FF9600'}
                onChange={(c) => {
                  const colors = [...s.colors]
                  colors[i] = c
                  onChange({ colors })
                }}
              />
            </Row>
          ))}
          <Row label="Thickness">
            <ThicknessField value={s.thickness} onChange={(t) => onChange({ thickness: t })} />
          </Row>

          <div className="my-2 h-px bg-border" />

          {/* Text colour — the legend value + price-scale tag. Defaults to following
              the line colour; set it when a pale line reads poorly on a light theme. */}
          <Row label="Text colour">
            <ColorField
              value={s.textColor ?? s.colors[0] ?? '#FF9600'}
              onChange={(c) => onChange({ textColor: c })}
            />
          </Row>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
